import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';

interface GitLabGroup {
  id: number;
  name: string;
  fullPath: string;
}

interface GitLabProject {
  id: number;
  name: string;
  fullPath: string;
}

interface TreeNode {
  group: GitLabGroup;
  expanded: boolean;
  children: TreeNode[];
  projects: GitLabProject[];
  loaded: boolean;
  loading: boolean;
}

interface GitLabTreePickerProps {
  /** Currently selected paths (for OIDC projectPaths) */
  selectedPaths: string[];
  /** Called when paths change */
  onChange: (paths: string[]) => void;
  /** External tree state (to persist across parent re-renders) */
  treeState?: any[];
  /** Called when tree state changes */
  onTreeStateChange?: (state: any[]) => void;
  /** Called when load status changes — true if API loaded successfully, false if failed */
  onLoadStatus?: (loaded: boolean) => void;
}

export const GitLabTreePicker: React.FC<GitLabTreePickerProps> = ({
  selectedPaths,
  onChange,
  treeState: externalTree,
  onTreeStateChange,
  onLoadStatus,
}) => {
  const [tree, setTreeInternal] = useState<TreeNode[]>(externalTree || []);
  const [loading, setLoading] = useState(!externalTree || externalTree.length === 0);

  const setTree = (newTree: TreeNode[] | ((prev: TreeNode[]) => TreeNode[])) => {
    setTreeInternal(prev => {
      const result = typeof newTree === 'function' ? newTree(prev) : newTree;
      if (onTreeStateChange) onTreeStateChange(result);
      return result;
    });
  };
  const [error, setError] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState<{ type: 'group' | 'project'; parentGroup: GitLabGroup } | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (!externalTree || externalTree.length === 0) loadTopLevel(); }, []);

  const loadTopLevel = async () => {
    try {
      setLoading(true);
      const resp = await ApiService.getGitLabGroups();
      setTree((resp.groups || []).map((g: GitLabGroup) => ({
        group: g, expanded: false, children: [], projects: [], loaded: false, loading: false,
      })));
      if (onLoadStatus) onLoadStatus(true);
    } catch (err: any) {
      setError(err.message);
      if (onLoadStatus) onLoadStatus(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = async (path: number[]) => {
    const updated = JSON.parse(JSON.stringify(tree));
    let node = getNode(updated, path);

    if (!node.loaded) {
      node.loading = true;
      setTree(updated);

      try {
        const [subResp, projResp] = await Promise.all([
          ApiService.getGitLabGroups(node.group.id),
          ApiService.getGitLabProjects(node.group.id),
        ]);
        node.children = (subResp.groups || []).map((g: GitLabGroup) => ({
          group: g, expanded: false, children: [], projects: [], loaded: false, loading: false,
        }));
        node.projects = projResp.projects || [];
        node.loaded = true;
      } catch (err) {
        console.error('Failed to load:', err);
      }
      node.loading = false;
      node.expanded = true;
    } else {
      node.expanded = !node.expanded;
    }
    setTree([...updated]);
  };

  const getNode = (nodes: TreeNode[], path: number[]): TreeNode => {
    let node = nodes[path[0]];
    for (let i = 1; i < path.length; i++) node = node.children[path[i]];
    return node;
  };

  const addPath = (fullPath: string, isGroup: boolean) => {
    const pathToAdd = isGroup ? `${fullPath}/*` : fullPath;
    console.log('[TreePicker] addPath called:', { fullPath, isGroup, pathToAdd, currentPaths: selectedPaths });
    if (!selectedPaths.includes(pathToAdd)) {
      const newPaths = [...selectedPaths, pathToAdd];
      console.log('[TreePicker] calling onChange with:', newPaths);
      onChange(newPaths);
    } else {
      console.log('[TreePicker] path already selected, skipping');
    }
  };

  const removePath = (pathToRemove: string) => {
    onChange(selectedPaths.filter(p => p !== pathToRemove));
  };

  const handleCreate = async () => {
    if (!newName || !createMode) return;
    setCreating(true);
    try {
      if (createMode.type === 'project') {
        const resp = await ApiService.createGitLabProject(newName, createMode.parentGroup.id);
        addPath(resp.project.fullPath, false);
      } else {
        // Create group via API (need to add this endpoint)
        // For now just add the path
        addPath(`${createMode.parentGroup.fullPath}/${newName}`, true);
      }
      setCreateMode(null);
      setNewName('');
      // Reload the parent
      loadTopLevel();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const renderNode = (node: TreeNode, path: number[], depth: number = 0): React.ReactNode => {
    const groupPath = `${node.group.fullPath}/*`;
    const isGroupSelected = selectedPaths.includes(groupPath);

    return (
      <div key={node.group.id} style={{ marginLeft: depth * 20 }}>
        <div
          className="d-flex align-items-center py-1 px-2 rounded"
          onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'}
          onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
        >
          {/* Expand toggle */}
          <span
            onClick={() => toggleExpand(path)}
            style={{ width: 20, cursor: 'pointer', textAlign: 'center' }}
          >
            {node.loading ? '⏳' : node.expanded ? '▼' : '▶'}
          </span>

          {/* Group name */}
          <span className="flex-grow-1 ms-1" onClick={() => toggleExpand(path)} style={{ cursor: 'pointer' }}>
            📁 {node.group.name}
          </span>

          {/* Add group (wildcard) */}
          <button
            type="button" className={`btn btn-sm ${isGroupSelected ? 'btn-success' : 'btn-outline-success'} me-1`}
            onClick={() => addPath(node.group.fullPath, true)}
            title={`Add ${node.group.fullPath}/* to OIDC paths`}
            disabled={isGroupSelected}
          >
            {isGroupSelected ? '✓ Group' : '+ Group'}
          </button>

          {/* Create inside this group */}
          <button
            type="button" className="btn btn-sm btn-outline-primary"
            onClick={() => setCreateMode({ type: 'project', parentGroup: node.group })}
            title="Create new project here"
          >
            + Repo
          </button>
        </div>

        {/* Children */}
        {node.expanded && (
          <>
            {node.children.map((child, i) => renderNode(child, [...path, i], depth + 1))}
            {node.projects.map(project => {
              const isSelected = selectedPaths.includes(project.fullPath);
              return (
                <div
                  key={project.id}
                  className="d-flex align-items-center py-1 px-2 rounded"
                  style={{ marginLeft: 20, backgroundColor: isSelected ? '#e8f4fd' : undefined }}
                  onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                  onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                >
                  <span style={{ width: 20 }}></span>
                  <span className="flex-grow-1 ms-1">🔀 {project.name}</span>
                  <button
                    type="button"
                    className={`btn btn-sm ${isSelected ? 'btn-info' : 'btn-outline-info'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('[TreePicker] Project button clicked:', project.fullPath, 'isSelected:', isSelected);
                      if (!isSelected) addPath(project.fullPath, false);
                    }}
                  >
                    {isSelected ? '✓ Selected' : '+ Select'}
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-3 text-center">Loading groups...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div>
      {/* Selected paths */}
      {selectedPaths.length > 0 && (
        <div className="mb-3">
          <label className="form-label fw-bold">OIDC Project Paths:</label>
          <div className="d-flex flex-wrap gap-1">
            {selectedPaths.map(p => (
              <span key={p} className="badge bg-primary d-flex align-items-center gap-1 py-2 px-2">
                {p}
                <button
                  type="button"
                  className="btn btn-sm p-0 ms-1 text-white"
                  style={{ lineHeight: 1, fontSize: '1em' }}
                  onClick={() => removePath(p)}
                  title="Remove"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Create form */}
      {createMode && (
        <div className="alert alert-info d-flex align-items-center gap-2 mb-2">
          <span>Create {createMode.type} in <strong>{createMode.parentGroup.fullPath}</strong>:</span>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 200 }}
            placeholder={`${createMode.type} name`}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <button type="button" className="btn btn-sm btn-success" onClick={handleCreate} disabled={!newName || creating}>
            {creating ? '...' : 'Create'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCreateMode(null)}>Cancel</button>
        </div>
      )}

      {/* Tree */}
      <div className="border rounded p-2" style={{ maxHeight: 350, overflowY: 'auto' }}>
        {tree.map((node, i) => renderNode(node, [i]))}
      </div>
    </div>
  );
};
